import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { FC, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { TFunction } from 'i18next';

type ThresholdLine = {
  label: string;
  value: number;
  color: string;
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

export const LiverLineChart: FC<{
  labels: string[];
  pastData: number[];
  forecastData: (number | null)[];
  latestIndex: string;
  thresholdLines?: ThresholdLine[];
  t?: TFunction;
}> = ({
  labels,
  pastData,
  forecastData,
  latestIndex,
  thresholdLines = [],
  t,
}) => {
  const pastLabel = t ? t('pages.predictionMap.stationDetail.chart.pastEstimate') : 'Past estimate';
  const forecastLabel = t ? t('pages.predictionMap.stationDetail.chart.forecast') : 'Forecast';
  const latestLineLabel = t ? t('pages.predictionMap.stationDetail.chart.latestTime') : 'Latest time';
  const yAxisLabel = t ? t('pages.predictionMap.stationDetail.chart.riverWaterLevelAxis') : 'River water level [m]';

  const data = useMemo(() => {
    return {
      labels,
      datasets: [
        {
          label: pastLabel,
          data: pastData,
          borderColor: '#3366cc',
          backgroundColor: '#3366cc',
          tension: 0.4,
        },
        {
          label: forecastLabel,
          data: forecastData,
          borderColor: '#00aa00',
          backgroundColor: '#00aa00',
          tension: 0.4,
        },
      ],
    }
  }, [labels, pastData, forecastData, pastLabel, forecastLabel]);

  const options = useMemo(() => {
    return {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: false,
        },
        annotation: {
          annotations: {
            latestLine: {
              type: 'line',
              xMin: latestIndex,
              xMax: latestIndex,
              borderColor: 'red',
              borderWidth: 2,
              label: {
                content: latestLineLabel,
                enabled: true,
                position: 'start',
                color: 'red',
              },
            },
            ...thresholdLines.reduce((acc, threshold, index) => {
              acc[`thresholdLine${index}`] = {
                type: 'line',
                yMin: threshold.value,
                yMax: threshold.value,
                borderColor: threshold.color,
                borderWidth: 2,
                label: {
                  content: threshold.label,
                  enabled: false,
                },
              };
              return acc;
            }, {} as Record<string, unknown>),
          },
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: yAxisLabel,
          },
          beginAtZero: true,
        },
      },
    };
  }, [latestIndex, thresholdLines, latestLineLabel, yAxisLabel]);

  // @ts-expect-error optionsは握り潰す
  return options ? <Line data={data} options={options} /> : null;
};
